import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Printer, FileText, Calendar, CreditCard, User, DollarSign, CheckCircle, XCircle, Clock, Building2 } from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { Withdrawal, WithdrawalStatus } from "../../types/payment";
import { jsPDF } from "jspdf";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

interface WithdrawalReceiptDialogProps {
    isOpen: boolean;
    onClose: () => void;
    withdrawal: Withdrawal | null;
}

const WithdrawalReceiptDialog: React.FC<WithdrawalReceiptDialogProps> = ({ isOpen, onClose, withdrawal }) => {
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    if (!withdrawal || !isOpen) return null;

    const getStatusBadge = (status: number) => {
        switch (status) {
            case WithdrawalStatus.COMPLETED:
                return "bg-green-100 text-green-700";
            case WithdrawalStatus.PROCESSING:
                return "bg-yellow-100 text-yellow-700";
            case WithdrawalStatus.PENDING:
                return "bg-blue-100 text-blue-700";
            case WithdrawalStatus.FAILED:
                return "bg-red-100 text-red-700";
            case WithdrawalStatus.CANCELLED:
                return "bg-gray-100 text-gray-700";
            default:
                return "bg-gray-100 text-gray-700";
        }
    };

    const getStatusText = (status: number) => {
        switch (status) {
            case WithdrawalStatus.COMPLETED:
                return "Completed";
            case WithdrawalStatus.PROCESSING:
                return "Processing";
            case WithdrawalStatus.PENDING:
                return "Pending";
            case WithdrawalStatus.FAILED:
                return "Failed";
            case WithdrawalStatus.CANCELLED:
                return "Cancelled";
            default:
                return "Unknown";
        }
    };

    const getStatusIcon = (status: number) => {
        switch (status) {
            case WithdrawalStatus.COMPLETED:
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case WithdrawalStatus.PROCESSING:
                return <Clock className="h-5 w-5 text-yellow-600" />;
            case WithdrawalStatus.FAILED:
                return <XCircle className="h-5 w-5 text-red-600" />;
            default:
                return <Clock className="h-5 w-5 text-blue-600" />;
        }
    };

    const getCurrencyDisplay = (currency: string | null | undefined) => {
        return getDynamicCurrencySymbol();
    };

    const getMethodDisplay = () => {
        if (withdrawal.withdrawalMethod === "paypal") {
            return {
                name: "PayPal",
                details: withdrawal.paypalEmail || "N/A",
                id: withdrawal.paypalPayoutBatchId
            };
        } else if (withdrawal.withdrawalMethod === "bank_account") {
            return {
                name: "Bank Account",
                details: withdrawal.paymentMethod?.bankName 
                    ? `${withdrawal.paymentMethod.bankName} - ****${withdrawal.paymentMethod.last4}` 
                    : "Manual Bank Transfer",
                id: withdrawal.bankAccountId
            };
        } else {
            return {
                name: "Stripe Connect",
                details: "Bank Transfer",
                id: withdrawal.stripeTransferId
            };
        }
    };

    const methodInfo = getMethodDisplay();

    const generatePDF = async () => {
        if (!withdrawal) return;

        setIsGeneratingPDF(true);
        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 20;
            let yPosition = 20;

            // Helper function to add text
            const addText = (text: string, x: number, y: number, options: any = {}) => {
                doc.setFontSize(options.fontSize || 12);
                doc.setTextColor(options.color || "#000000");
                if (options.align === "center") {
                    doc.text(text, x, y, { align: "center" });
                } else if (options.align === "right") {
                    doc.text(text, x, y, { align: "right" });
                } else {
                    doc.text(text, x, y);
                }
            };

            // Helper function to add line
            const addLine = (y: number) => {
                doc.setDrawColor(200, 200, 200);
                doc.line(margin, y, pageWidth - margin, y);
            };

            // Header
            addText("KOKOBEO", pageWidth / 2, yPosition, { fontSize: 24, color: "#2563eb", align: "center" });
            yPosition += 10;
            addText("Withdrawal Receipt", pageWidth / 2, yPosition, { fontSize: 16, align: "center" });
            yPosition += 20;

            // Receipt ID and Date
            addText(`Receipt #: ${withdrawal.id}`, margin, yPosition);
            addText(`Date: ${new Date(withdrawal.createdAt).toLocaleDateString()}`, pageWidth - margin, yPosition, { align: "right" });
            yPosition += 15;

            addLine(yPosition);
            yPosition += 10;

            // Transaction Details
            addText("WITHDRAWAL DETAILS", margin, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 10;

            addText(`Status: ${getStatusText(withdrawal.status || WithdrawalStatus.PENDING).toUpperCase()}`, margin, yPosition);
            yPosition += 8;

            addText(`Method: ${methodInfo.name}`, margin, yPosition);
            yPosition += 8;

            if (methodInfo.id) {
                addText(`Transaction ID: ${methodInfo.id}`, margin, yPosition, { fontSize: 10 });
                yPosition += 8;
            }

            addText(`Created: ${new Date(withdrawal.createdAt).toLocaleString()}`, margin, yPosition);
            yPosition += 8;

            if (withdrawal.completedAt) {
                addText(`Completed: ${new Date(withdrawal.completedAt).toLocaleString()}`, margin, yPosition);
                yPosition += 8;
            }

            yPosition += 7;
            addLine(yPosition);
            yPosition += 10;

            // User Information
            if (withdrawal.user) {
                addText("USER INFORMATION", margin, yPosition, { fontSize: 14, color: "#374151" });
                yPosition += 10;

                const userName = `${withdrawal.user.firstName || ""} ${withdrawal.user.lastName || ""}`.trim() || "Unknown User";
                addText(`Name: ${userName}`, margin, yPosition);
                yPosition += 8;

                addText(`Email: ${withdrawal.user.email || "N/A"}`, margin, yPosition);
                yPosition += 15;

                addLine(yPosition);
                yPosition += 10;
            }

            // Amount Breakdown
            addText("AMOUNT BREAKDOWN", margin, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 10;

            const currency = getCurrencyDisplay(withdrawal.currency);
            const amount = withdrawal.amount || 0;

            addText("Withdrawal Amount:", margin, yPosition);
            addText(`${currency} ${amount.toFixed(2)}`, pageWidth - margin, yPosition, { align: "right" });
            yPosition += 8;

            addText("Currency:", margin, yPosition);
            addText(currency, pageWidth - margin, yPosition, { align: "right" });
            yPosition += 8;

            addLine(yPosition);
            yPosition += 8;

            addText("TOTAL AMOUNT:", margin, yPosition, { fontSize: 14, color: "#374151" });
            addText(`${currency} ${amount.toFixed(2)}`, pageWidth - margin, yPosition, { fontSize: 14, color: "#374151", align: "right" });
            yPosition += 15;

            addLine(yPosition);
            yPosition += 10;

            // Payment Method Information
            addText("PAYMENT METHOD", margin, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 10;

            addText(`Method: ${methodInfo.name}`, margin, yPosition);
            yPosition += 8;

            addText(`Details: ${methodInfo.details}`, margin, yPosition);
            yPosition += 8;

            if (methodInfo.id) {
                addText(`Transaction ID: ${methodInfo.id}`, margin, yPosition, { fontSize: 10 });
                yPosition += 15;
            } else {
                yPosition += 7;
            }

            // Notes
            if (withdrawal.notes) {
                addLine(yPosition);
                yPosition += 10;

                addText("NOTES", margin, yPosition, { fontSize: 14, color: "#374151" });
                yPosition += 10;

                const notes = withdrawal.notes.split("\n").slice(0, 3); // First 3 lines only
                notes.forEach(note => {
                    addText(note, margin, yPosition, { fontSize: 10 });
                    yPosition += 6;
                });
                yPosition += 10;
            }

            // Footer
            addLine(yPosition);
            yPosition += 10;
            addText("Thank you for using Kokobeo!", pageWidth / 2, yPosition, { fontSize: 12, color: "#6b7280", align: "center" });
            yPosition += 8;
            addText("This is an automated receipt. No signature required.", pageWidth / 2, yPosition, { fontSize: 10, color: "#9ca3af", align: "center" });

            // Save the PDF
            const fileName = `withdrawal-receipt-${withdrawal.id}-${new Date().toISOString().split("T")[0]}.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to generate PDF. Please try again.");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const printReceipt = () => {
        window.print();
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
                <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.95 }}
                    className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                >
                    {/* Header */}
                    <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-blue-50">
                        <div className="flex justify-between items-start">
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <FileText className="h-6 w-6 text-purple-600" />
                                    <h2 className="text-2xl font-bold text-gray-900">Withdrawal Receipt</h2>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Badge variant={undefined} className={getStatusBadge(withdrawal.status || WithdrawalStatus.PENDING)}>
                                        {getStatusText(withdrawal.status || WithdrawalStatus.PENDING).toUpperCase()}
                                    </Badge>
                                    <Badge variant="outline" className="border-purple-200 text-purple-700">
                                        {methodInfo.name.toUpperCase()}
                                    </Badge>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    Receipt #{withdrawal.id} • {new Date(withdrawal.createdAt).toLocaleDateString()}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Receipt Content */}
                    <div className="p-6 space-y-6" id="receipt-content">
                        {/* Company Header */}
                        <div className="text-center py-6 border-b">
                            <h1 className="text-3xl font-bold text-purple-600 mb-2">KOKOBEO</h1>
                            <p className="text-gray-600">Withdrawal Receipt</p>
                            <p className="text-sm text-gray-500 mt-1">
                                {new Date(withdrawal.createdAt).toLocaleString()}
                            </p>
                        </div>

                        {/* Status Summary */}
                        <Card className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {getStatusIcon(withdrawal.status || WithdrawalStatus.PENDING)}
                                    <div>
                                        <h3 className="font-semibold text-gray-900">
                                            {getStatusText(withdrawal.status || WithdrawalStatus.PENDING)}
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {withdrawal.status === WithdrawalStatus.COMPLETED && withdrawal.completedAt
                                                ? `Completed on ${new Date(withdrawal.completedAt).toLocaleDateString()}`
                                                : withdrawal.status === WithdrawalStatus.PROCESSING
                                                ? "Currently processing"
                                                : withdrawal.status === WithdrawalStatus.FAILED
                                                ? "Withdrawal failed"
                                                : "Pending processing"}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-red-600">
                                        -{getCurrencyDisplay(withdrawal.currency)} {(withdrawal.amount || 0).toFixed(2)}
                                    </p>
                                    <p className="text-sm text-gray-600">Withdrawal Amount</p>
                                </div>
                            </div>
                        </Card>

                        {/* Transaction Summary */}
                        <Card className="p-6 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">Withdrawal Information</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Receipt ID:</span>
                                            <span className="font-medium">{withdrawal.id}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Status:</span>
                                            <Badge variant={undefined} className={getStatusBadge(withdrawal.status || WithdrawalStatus.PENDING)}>
                                                {getStatusText(withdrawal.status || WithdrawalStatus.PENDING)}
                                            </Badge>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Method:</span>
                                            <span className="font-medium">{methodInfo.name}</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">Amount Summary</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Amount:</span>
                                            <span className="font-bold text-lg text-red-600">
                                                -{getCurrencyDisplay(withdrawal.currency)} {(withdrawal.amount || 0).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Currency:</span>
                                            <span className="font-medium">{getCurrencyDisplay(withdrawal.currency)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* User Information */}
                        {withdrawal.user && (
                            <Card className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <User className="h-5 w-5 text-blue-600" />
                                    <h3 className="font-semibold text-gray-900">User Information</h3>
                                </div>
                                <div className="space-y-2">
                                    <p className="font-medium text-lg">
                                        {withdrawal.user.firstName} {withdrawal.user.lastName}
                                    </p>
                                    <p className="text-gray-600">{withdrawal.user.email}</p>
                                </div>
                            </Card>
                        )}

                        {/* Payment Method Details */}
                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                {withdrawal.withdrawalMethod === "paypal" ? (
                                    <DollarSign className="h-5 w-5 text-purple-600" />
                                ) : withdrawal.withdrawalMethod === "bank_account" ? (
                                    <Building2 className="h-5 w-5 text-green-600" />
                                ) : (
                                    <CreditCard className="h-5 w-5 text-blue-600" />
                                )}
                                <h3 className="font-semibold text-gray-900">Payment Method Details</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-gray-600">Method:</span>
                                    <p className="font-medium">{methodInfo.name}</p>
                                </div>
                                <div>
                                    <span className="text-gray-600">Details:</span>
                                    <p className="font-medium">{methodInfo.details}</p>
                                </div>
                                {methodInfo.id && (
                                    <div className="col-span-2">
                                        <span className="text-gray-600">Transaction ID:</span>
                                        <p className="font-medium font-mono text-sm break-all">
                                            {methodInfo.id}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <span className="text-gray-600">Created:</span>
                                    <p className="font-medium">
                                        {new Date(withdrawal.createdAt).toLocaleString()}
                                    </p>
                                </div>
                                {withdrawal.processedAt && (
                                    <div>
                                        <span className="text-gray-600">Processed:</span>
                                        <p className="font-medium">
                                            {new Date(withdrawal.processedAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                                {withdrawal.completedAt && (
                                    <div>
                                        <span className="text-gray-600">Completed:</span>
                                        <p className="font-medium">
                                            {new Date(withdrawal.completedAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Additional Information */}
                        {(withdrawal.notes || withdrawal.failureReason) && (
                            <Card className="p-6">
                                <h3 className="font-semibold text-gray-900 mb-4">Additional Information</h3>
                                <div className="space-y-4">
                                    {withdrawal.failureReason && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <span className="text-gray-600 font-medium">Failure Reason:</span>
                                            <p className="text-red-700 mt-1">{withdrawal.failureReason}</p>
                                        </div>
                                    )}
                                    {withdrawal.notes && (
                                        <div>
                                            <span className="text-gray-600 font-medium">Notes:</span>
                                            <p className="font-medium text-gray-700 mt-1 whitespace-pre-wrap">{withdrawal.notes}</p>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* Footer */}
                        <div className="text-center py-6 border-t">
                            <p className="text-gray-600 mb-2">Thank you for using Kokobeo!</p>
                            <p className="text-sm text-gray-500">This is an automated receipt. No signature required.</p>
                            <p className="text-xs text-gray-400 mt-2">
                                For support, contact: support@kokobeo.com
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="p-6 border-t bg-gray-50">
                        <div className="flex flex-col sm:flex-row gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={printReceipt}
                                className="flex items-center gap-2"
                            >
                                <Printer className="h-4 w-4" />
                                Print Receipt
                            </Button>
                            <Button
                                onClick={generatePDF}
                                disabled={isGeneratingPDF}
                                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                            >
                                <Download className="h-4 w-4" />
                                {isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default WithdrawalReceiptDialog;





